import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTodayBriefing,
  regenerateBriefing,
  isActiveBriefing,
  type BriefingResponse,
  type BriefingItem,
} from '../api/briefing';

/**
 * "Your daily briefing" card — the first thing on the agent dashboard.
 *
 * The server picks the content by the member's resolved focus (agent /
 * operations / tc / listing_marketing) and phrases it with Claude. This
 * component just renders the returned doc: a headline, then each section
 * (label + item rows). Tone drives color: urgent in deep red, good in the
 * success token, normal muted-to-body.
 *
 * Self-hides (returns null) while the first generation is in flight only if
 * there is truly nothing yet, and whenever focus is 'none' or the doc is
 * empty. Sentence case, no em dashes, matching the calm voice of the doc.
 */

const ACCENT_URGENT = '#A32D2D';

function toneColor(tone: BriefingItem['tone']): string {
  if (tone === 'urgent') return ACCENT_URGENT;
  if (tone === 'good') return 'var(--success)';
  return 'var(--text-body)';
}

function updatedLabel(generatedAt: number): string {
  try {
    return new Date(generatedAt * 1000).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/**
 * The server writes headlines like "Good morning, Daniel. Here is where the
 * team stands." Home already renders a time-aware greeting directly above
 * this card, so we ended up with two greetings 100px apart that disagreed
 * with each other — the page said "Good evening" while a briefing generated
 * at 6am still said "Good morning". Strip the greeting clause and keep the
 * substance. (Daniel 2026-07-27.)
 */
function stripGreeting(headline: string): string {
  const out = headline.replace(
    /^\s*(good\s+(morning|afternoon|evening)|hi|hey|hello)\b[^.!?]*[.!?]\s*/i,
    '',
  );
  return out.trim() || headline;
}

export default function DailyBriefingCard() {
  const qc = useQueryClient();
  // Collapsed by default (Daniel 2026-07-27). This is long-form reading and
  // it was eating the entire first screen — on a phone the briefing alone
  // was three scrolls before anything actionable. The headline IS the
  // briefing for most mornings; the detail is opt-in. Choice persists.
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('tr_briefing_open') === '1'; } catch { return false; }
  });
  function toggle() {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem('tr_briefing_open', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }
  const { data, isLoading } = useQuery<BriefingResponse>({
    queryKey: ['daily-briefing', 'today'],
    queryFn: getTodayBriefing,
    staleTime: 5 * 60_000,
  });

  const regen = useMutation({
    mutationFn: regenerateBriefing,
    onSuccess: (fresh) => {
      qc.setQueryData(['daily-briefing', 'today'], fresh);
    },
  });

  // First load: the server may be generating on demand. Show a subtle
  // placeholder rather than flashing nothing, but only while we genuinely
  // have no data yet.
  if (isLoading) {
    return (
      <section style={cardShell}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Preparing your daily briefing…</div>
      </section>
    );
  }

  // Nothing to show for this user.
  if (!isActiveBriefing(data)) return null;
  if (data.sections.length === 0) return null;

  return (
    <section style={cardShell}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              marginBottom: 4,
            }}
          >
            Your daily briefing
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 18,
              fontWeight: 600,
              color: 'var(--text-heading)',
              margin: 0,
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
            }}
          >
            {stripGreeting(data.headline)}
          </h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '5px 12px',
              minHeight: 32,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-body)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            {open ? 'Hide' : `Read${data.sections.length ? ` (${data.sections.length})` : ''}`}
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                transition: 'transform 160ms var(--ease)',
                transform: open ? 'rotate(90deg)' : 'none',
                fontSize: 10,
              }}
            >▸</span>
          </button>
          <button
            type="button"
            onClick={() => regen.mutate()}
            disabled={regen.isPending}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '5px 12px',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-body)',
              cursor: regen.isPending ? 'wait' : 'pointer',
            }}
          >
            {regen.isPending ? 'Refreshing…' : 'Regenerate'}
          </button>
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Updated {updatedLabel(data.generated_at)}
          </span>
        </div>
      </div>

      <div style={{ display: open ? 'flex' : 'none', flexDirection: 'column', gap: 16 }}>
        {data.sections.map((s, si) => (
          <div key={`${si}-${s.label}`}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                marginBottom: 8,
                paddingBottom: 6,
                borderBottom: '1px solid var(--border)',
              }}
            >
              {s.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {s.items.map((item, ii) => (
                <div
                  key={ii}
                  style={{
                    fontSize: 13,
                    lineHeight: 1.45,
                    color: toneColor(item.tone),
                    fontWeight: item.tone === 'normal' ? 400 : 500,
                  }}
                >
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const cardShell = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '18px 20px',
  marginBottom: 28,
} as const;
